import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Table, 
  Button, 
  Input, 
  Select, 
  Space, 
  Tag, 
  Typography, 
  Card,
  Modal,
  Form,
  message,
  Avatar,
  Tooltip,
  Row,
  Col,
  Statistic
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  LockOutlined,
  UnlockOutlined,
  TeamOutlined,
  UserAddOutlined,
  UserDeleteOutlined
} from '@ant-design/icons';
import useAuthStore from '../../store/authStore';
import apiService from '../../services/apiService';
import { avatarUrl } from '../../utils/avatarUrl';

const { Title, Text } = Typography;
const { Option } = Select;

const AdminUsersPage = () => {
  const { user: currentUser } = useAuthStore();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [stats, setStats] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);

  useEffect(() => {
    fetchUserStats();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [searchText, roleFilter, statusFilter, currentPage, pageSize]);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchText) params.append('search', searchText);
      if (roleFilter) params.append('role', roleFilter);
      if (statusFilter) params.append('status', statusFilter);
      params.append('page', currentPage);
      params.append('limit', pageSize);
      
      const response = await apiService.get(`/admin/users?${params.toString()}`);
      setUsers(response.data.data || []);
      if (response.data.pagination) {
        setTotalUsers(response.data.pagination.total || 0);
      } else {
        setTotalUsers(response.data.data ? response.data.data.length : 0);
      }
    } catch (error) {
      message.error('Failed to fetch users');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await apiService.get('/admin/stats/users');
      setStats(response.data.data);
    } catch (error) {
      console.error('Failed to fetch user stats:', error);
    }
  };

  const handleCreateUser = async (values) => {
    try {
      const response = await apiService.post('/admin/users', values);
      if (response.data.code === 'USER_CREATED') {
        message.success('User created successfully');
        setCreateModalVisible(false);
        form.resetFields();
        fetchUsers();
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to create user');
    }
  };

  const handleUpdateUser = async (values) => {
    try {
      const response = await apiService.put(`/admin/users/${selectedUser._id}`, values);
      message.success('User updated successfully');
      setEditModalVisible(false);
      setSelectedUser(null);
      form.resetFields();
      fetchUsers();
      fetchUserStats();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to update user');
    }
  };

  const handleToggleUserStatus = async (userId, isActive) => {
    try {
      await apiService.patch(`/admin/users/${userId}`, { isActive: !isActive });
      message.success(`User ${!isActive ? 'activated' : 'deactivated'} successfully`);
      fetchUsers();
      fetchUserStats();
    } catch (error) {
      message.error('Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId) => {
    Modal.confirm({
      title: 'Delete User',
      content: 'Are you sure you want to delete this user? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await apiService.delete(`/admin/users/${userId}`);
          message.success('User deleted successfully');
          fetchUsers();
          fetchUserStats();
        } catch (error) {
          message.error('Failed to delete user');
        }
      }
    });
  };

  const getRoleColor = (role) => {
    const colors = {
      super_admin: 'red',
      tenant_admin: 'orange',
      crm_user: 'blue'
    };
    return colors[role] || 'default';
  };

  const handleTableChange = (paginationInfo, filtersInfo, sorter) => {
    setCurrentPage(paginationInfo.current);
    setPageSize(paginationInfo.pageSize);
    // Handle table column filters
    if (filtersInfo.role && filtersInfo.role.length > 0) {
      setRoleFilter(filtersInfo.role[0]);
    } else {
      setRoleFilter(null);
    }
  };

  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_, record) => (
        <div className="flex items-center space-x-3">
          <Avatar
            icon={<UserOutlined />}
            src={avatarUrl(record.avatar)}
            className={record.isActive ? '' : 'opacity-50'}
          />
          <div>
            <Text strong className={record.isActive ? '' : 'opacity-50'}>
              {record.fullName}
            </Text>
            <br />
            <Text className={`text-sm ${record.isActive ? 'text-gray-500' : 'text-gray-400'}`}>
              {record.email}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color={getRoleColor(role)} className="capitalize">
          {role.replace('_', ' ')}
        </Tag>
      ),
      filters: [
        { text: 'Super Admin', value: 'super_admin' },
        { text: 'Tenant Admin', value: 'tenant_admin' },
        { text: 'CRM User', value: 'crm_user' },
      ],
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'error'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Last Login',
      dataIndex: 'lastLogin',
      key: 'lastLogin',
      render: (date) => date ? new Date(date).toLocaleDateString() : 'Never',
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Edit User">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                setSelectedUser(record);
                form.setFieldsValue(record);
                setEditModalVisible(true);
              }}
              className="focus-visible:focus"
            />
          </Tooltip>
          
          <Tooltip title={record.isActive ? 'Deactivate User' : 'Activate User'}>
            <Button
              type="text"
              icon={record.isActive ? <LockOutlined /> : <UnlockOutlined />}
              onClick={() => handleToggleUserStatus(record._id, record.isActive)}
              className="focus-visible:focus"
              disabled={record._id === currentUser._id}
            />
          </Tooltip>
          
          <Tooltip title="Delete User">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteUser(record._id)}
              className="focus-visible:focus"
              disabled={record._id === currentUser._id}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <Title level={2} className="mb-2">User Management</Title>
          <Text className="text-gray-600 dark:text-gray-400">
            Manage users and their permissions
          </Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateModalVisible(true)}
          className="touch-target focus-visible:focus"
        >
          Add User
        </Button>
      </div>

      {/* Statistics */}
      {stats && (
        <Row gutter={16} className="mb-6">
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Total Users"
                value={stats.total}
                prefix={<TeamOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Active Users"
                value={stats.active}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Admins"
                value={stats.admins}
                prefix={<UserAddOutlined />}
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="CRM Users"
                value={stats.users}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Card className="shadow-soft">
        <Row gutter={16} className="mb-4">
          <Col xs={24} sm={8}>
            <Input
              placeholder="Search users..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={8}>
            <Select
              placeholder="Filter by role"
              value={roleFilter}
              onChange={setRoleFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="tenant_admin">Tenant Admin</Option>
              <Option value="crm_user">CRM User</Option>
            </Select>
          </Col>
          <Col xs={24} sm={8}>
            <Select
              placeholder="Filter by status"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Users Table */}
      <Card className="shadow-soft">
        <Table
          columns={columns}
          dataSource={users}
          rowKey="_id"
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalUsers,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} users`,
            pageSizeOptions: ['10', '20', '50', '100']
          }}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          onRow={(record) => ({
            onClick: () => navigate(`/admin/users/${record._id}`),
            style: { cursor: 'pointer' }
          })}
        />
      </Card>

      {/* Create User Modal */}
      <Modal
        title="Create New User"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={async (values) => {
            await handleCreateUser(values);
            fetchUserStats();
          }}
        >
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              name="firstName"
              label="First Name"
              rules={[{ required: true, message: 'Please enter first name' },
                {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\s|\s$/.test(value)) {
                    return Promise.reject(new Error("No leading or trailing spaces allowed"));
                  }
                  return Promise.resolve();
                },
              },
              ]}
            >
              <Input className="focus-visible:focus" />
            </Form.Item>

            <Form.Item
              name="lastName"
              label="Last Name"
              rules={[{ required: true, message: 'Please enter last name' },
                {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (/^\s|\s$/.test(value)) {
                    return Promise.reject(new Error("No leading or trailing spaces allowed"));
                  }
                  return Promise.resolve();
                },
              },
              ]}
            >
              <Input className="focus-visible:focus" />
            </Form.Item>
          </div>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter valid email' }
            ]}
          >
            <Input className="focus-visible:focus" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Please enter password' },
              { min: 8, message: 'Password must be at least 8 characters' },
              {
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                message: 'Password must contain uppercase, lowercase, number and special character (@$!%*?&)'
              }
            ]}
            help="Must contain: uppercase, lowercase, number and special character (@$!%*?&)"
          >
            <Input.Password className="focus-visible:focus" />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select role' }]}
          >
            <Select className="focus-visible:focus">
              {currentUser.role === 'super_admin' && (
                <Option value="tenant_admin">Tenant Admin</Option>
              )}
              <Option value="crm_user">CRM User</Option>
            </Select>
          </Form.Item>

          <div className="flex justify-end space-x-2">
            <Button onClick={() => {
              setCreateModalVisible(false);
              form.resetFields();
            }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              Create User
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        title="Edit User"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          setSelectedUser(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdateUser}
        >
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              name="firstName"
              label="First Name"
              rules={[{ required: true, message: 'Please enter first name' }]}
            >
              <Input className="focus-visible:focus" />
            </Form.Item>

            <Form.Item
              name="lastName"
              label="Last Name"
              rules={[{ required: true, message: 'Please enter last name' }]}
            >
              <Input className="focus-visible:focus" />
            </Form.Item>
          </div>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter valid email' }
            ]}
          >
            <Input className="focus-visible:focus" />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select role' }]}
          >
            <Select className="focus-visible:focus">
              {currentUser.role === 'super_admin' && (
                <Option value="tenant_admin">Tenant Admin</Option>
              )}
              <Option value="crm_user">CRM User</Option>
            </Select>
          </Form.Item>

          <div className="flex justify-end space-x-2">
            <Button onClick={() => {
              setEditModalVisible(false);
              setSelectedUser(null);
              form.resetFields();
            }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit">
              Update User
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminUsersPage;
